import { useId, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useToast } from '@/components/feedback/Toast';
import { EditorPage } from '@/components/layout/EditorPage';
import { Page, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { can } from '@/lib/permissions';
import { useUnsavedChanges } from '@/lib/useUnsavedChanges';

import type { WebsiteSectionConfig } from './sections';
import { WebsiteEditor } from './WebsiteEditor';
import { WebsiteText } from './WebsiteFields';
import { useSaveWebsiteEntry, useWebsiteSection } from './websiteApi';
import styles from './WebsiteContent.module.css';

export function NewWebsiteEntryPage({ section }: { section: WebsiteSectionConfig }) {
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const toast = useToast();
  const formId = useId();
  const [title, setTitle] = useState('');
  const [customSlug, setCustomSlug] = useState<string | null>(null);
  const slug =
    customSlug ??
    title
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 160)
      .replace(/-$/g, '');
  const save = useSaveWebsiteEntry();
  const unsaved = useUnsavedChanges(Boolean(title || customSlug) || save.isPending);

  function close() {
    void navigate(section.path, { replace: true });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (save.isPending) return;
    try {
      const entry = await save.mutateAsync({
        entry: { kind: section.kind, title, slug, state: 'draft' },
      });
      unsaved.allowSavedExit();
      toast.success('Draft created.');
      if (can(user, 'website', 'edit')) {
        void navigate(`${section.path}/${entry.id}/edit`, { replace: true });
      } else {
        close();
      }
    } catch {
      // The API message remains on the page.
    }
  }

  return (
    <EditorPage
      title={`New ${section.item}`}
      description="Create a draft to start adding content."
      backTo={section.path}
      backLabel={`Back to ${section.title}`}
      unsaved={unsaved}
      busy={save.isPending}
      actions={
        <>
          <Button variant="ghost" onClick={close} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={save.isPending}>
            Create draft
          </Button>
        </>
      }
    >
      {save.error ? (
        <p className={styles.error} role="alert">
          {save.error.message}
        </p>
      ) : null}
      <form id={formId} className={styles.newDraft} onSubmit={(event) => void submit(event)}>
        <Panel title="Draft details">
          <div className={styles.stack}>
            <WebsiteText
              label={section.kind === 'faq' ? 'Question' : 'Title'}
              value={title}
              onChange={setTitle}
              required
            />
            <WebsiteText
              label="Link name"
              value={slug}
              onChange={setCustomSlug}
              hint="Use lowercase letters, numbers and hyphens. The link name stays fixed after creation."
              required
            />
          </div>
        </Panel>
      </form>
    </EditorPage>
  );
}

export function WebsiteEditorPage({ section }: { section: WebsiteSectionConfig }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const entries = useWebsiteSection(section.kind);

  if (entries.isPending)
    return (
      <Page>
        <Spinner label="Loading website content" />
      </Page>
    );
  if (entries.isError)
    return (
      <Page>
        <ErrorState
          message="The website content could not be loaded."
          onRetry={() => {
            void entries.refetch();
          }}
        />
      </Page>
    );
  const entry = entries.data.items.find((item) => item.id === id);
  if (!entry)
    return (
      <Page>
        <EmptyState
          title="Website entry unavailable"
          body="Go back to the section to choose another entry."
        />
      </Page>
    );
  return (
    <WebsiteEditor
      key={entry.id}
      initial={entry}
      section={section}
      onClose={() => {
        void navigate(section.path, { replace: true });
      }}
    />
  );
}
